import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { apiService } from '../services/api';
import { BirdType } from '../types';

const About: React.FC = () => {
  const { isAuthenticated } = useAuthStore();
  const [birds, setBirds] = useState<BirdType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBirds = async () => {
      try {
        const birdData = await apiService.getBirdTypes();
        setBirds(birdData);
      } catch (error) {
        console.error('Failed to fetch bird types:', error);
        setBirds([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBirds();
  }, []);

  return (
    <div className="space-y-16">
      {/* Hero Section */}
      <section className="text-center py-12">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-6">
          About Neubird Messaging
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
          Revolutionizing communication through the ancient art of avian message delivery, 
          enhanced with modern technology and reliability.
        </p>
      </section>

      {/* Story Section */}
      <section className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-8 md:p-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6">Our Story</h2>
          <div className="prose prose-lg max-w-none text-gray-600 dark:text-gray-300">
            <p className="mb-6">
              Founded in 2024, Neubird Messaging combines the timeless reliability of carrier pigeons 
              with cutting-edge tracking technology and a diverse fleet of messenger birds. What started 
              as a passion project by ornithology enthusiasts has grown into the world's premier 
              avian messaging service.
            </p>
            <p className="mb-6">
              Our mission is simple: provide a unique, eco-friendly, and delightfully analog way to 
              send messages in our increasingly digital world. Whether you need to send an urgent 
              business communication or a heartfelt personal note, our trained birds are ready to 
              carry your message across any distance.
            </p>
            <p>
              Every bird in our fleet is treated with the utmost care and respect. They undergo 
              comprehensive training, receive regular health check-ups, and enjoy comfortable 
              accommodations at our state-of-the-art aviaries around the world.
            </p>
          </div>
        </div>
      </section>

      {/* Bird Fleet Details */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-gray-100 mb-12">
            Meet Our Messenger Fleet
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {loading ? (
              <div className="col-span-full text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-300">Loading our feathered messengers...</p>
              </div>
            ) : birds.length === 0 ? (
              <div className="col-span-full text-center py-8">
                <p className="text-gray-600 dark:text-gray-300">No birds available at the moment. Please try again later.</p>
              </div>
            ) : birds.map((bird) => (
              <div key={bird.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden bird-card">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1">
                        {bird.name}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                        {bird.species}
                      </p>
                    </div>
                    <div className="text-2xl">🦅</div>
                  </div>
                  
                  <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm">
                    {bird.description}
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Max Message:</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{bird.properties.maxMessageLength} chars</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Max Distance:</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{bird.properties.maxDistance}km</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Speed:</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{bird.properties.speed} km/h</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Base Cost:</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">${bird.properties.baseCost}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Per km:</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">${bird.properties.costPerKm}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Reliability:</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{bird.properties.reliability}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-8 md:p-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-gray-100 mb-12">
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📝</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                1. Compose
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Write your message and select your destination on our interactive map
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🦅</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                2. Choose Bird
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Select the perfect bird based on distance, speed, and message length
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">💳</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                3. Checkout
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Review the cost and confirm your message for delivery
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📍</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                4. Track
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Follow your message's journey in real-time until delivery
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sustainability Section */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6">
            Sustainable & Eco-Friendly
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
            Unlike traditional delivery methods, our avian messengers produce zero emissions 
            and follow natural migration patterns. We're committed to environmental 
            sustainability while preserving the ancient art of bird messaging.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-3xl mb-3">🌱</div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Zero Emissions</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Completely carbon-neutral message delivery
              </p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-3">🏥</div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Bird Welfare</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Regular health checks and comfortable housing
              </p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-3">🌍</div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Conservation</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Supporting wildlife conservation efforts worldwide
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-600 dark:bg-blue-700 text-white rounded-2xl p-8 md:p-12 text-center">
        <h2 className="text-3xl font-bold mb-4">
          Ready to Experience Neubird?
        </h2>
        <p className="text-xl mb-8 opacity-90">
          Join our community and send your first message with our feathered messengers.
        </p>
        {isAuthenticated ? (
          <Link
            to="/send"
            className="inline-flex items-center px-8 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 dark:bg-gray-100 dark:text-blue-700 dark:hover:bg-gray-200 transition-colors"
          >
            Send Your First Message
            <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        ) : (
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="px-8 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 dark:bg-gray-100 dark:text-blue-700 dark:hover:bg-gray-200 transition-colors"
            >
              Get Started Free
            </Link>
            <Link
              to="/login"
              className="px-8 py-3 border border-white text-white font-semibold rounded-lg hover:bg-blue-700 dark:hover:bg-blue-800 transition-colors"
            >
              Sign In
            </Link>
          </div>
        )}
      </section>
    </div>
  );
};

export default About;
